/* Maison Milan — Shop by video
 *
 * The card row opens a full-screen vertical feed. Swipe up for the next
 * video, the product stays on screen, and adding to the bag never leaves the
 * player — the whole point of the pattern is that watching and buying are the
 * same gesture.
 *
 * The player builds its slides from the section's JSON rather than cloning
 * the cards, so the row stays a plain list and the player can hold state the
 * cards never need.
 */
(() => {
  const root = document.querySelector('[data-video-feed]');
  if (!root) return;

  let feed = [];
  try {
    feed = JSON.parse(root.textContent) || [];
  } catch (e) {
    return;
  }
  if (feed.length === 0) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const money = (n) => n;

  /* ---- The card row ------------------------------------------------------ */
  const track = document.querySelector('[data-video-track]');

  /* The arrows wrap rather than dead-ending: at the right-hand edge the next
   * arrow returns to the start, and the previous arrow from the start goes to
   * the end. A control that stops doing anything is indistinguishable from
   * one that has broken. */
  const nudge = (dir) => {
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const smooth = reduced.matches ? 'auto' : 'smooth';

    /* An explicit, clamped target rather than a relative scrollBy. The track
     * carries scroll-snap, and a relative smooth scroll whose landing point
     * is past the last snap position gets snapped back to where it started:
     * on a wide screen this row overflows by only 168px while the step asked
     * for is 0.8 of a 1300px viewport, so every press overshot and the arrow
     * did nothing at all. */
    let target;
    if (dir > 0 && track.scrollLeft >= max - 4) target = 0;
    else if (dir < 0 && track.scrollLeft <= 4) target = max;
    else target = Math.max(0, Math.min(track.scrollLeft + track.clientWidth * 0.8 * dir, max));

    track.scrollTo({ left: target, behavior: smooth });
    window.setTimeout(() => {
      if (Math.abs(track.scrollLeft - target) > 2) track.scrollLeft = target;
    }, 420);
  };

  document.querySelector('[data-video-next]')?.addEventListener('click', () => nudge(1));
  document.querySelector('[data-video-prev]')?.addEventListener('click', () => nudge(-1));

  /* Category filter on the row. Hiding rather than removing, so the scroll
   * position and the cards' own forms survive a filter change. */
  document.querySelectorAll('[data-video-cat]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cat = btn.getAttribute('data-video-cat');
      document.querySelectorAll('[data-video-cat]').forEach((b) => b.classList.toggle('is-active', b === btn));
      document.querySelectorAll('[data-video-item]').forEach((item) => {
        item.hidden = cat !== 'all' && item.getAttribute('data-cat') !== cat;
      });
      track?.scrollTo({ left: 0, behavior: reduced.matches ? 'auto' : 'smooth' });
    });
  });


  /* ---- Card previews -----------------------------------------------------
   * A row of still posters is a row of pictures. A row where the card under
   * your thumb is playing is a feed. Only one plays at a time, it is muted
   * and it stops the moment the card leaves the middle of the screen, so the
   * row never becomes four videos fighting for bandwidth.
   */
  if (!reduced.matches && 'IntersectionObserver' in window) {
    const previews = new Map();

    /* Data saver and 2g: a 9:16 film is the most expensive thing on the page
     * and nobody on a metered connection asked for five of them. The posters
     * stay, the play badge stays, and tapping a card still opens the player. */
    const conn = navigator.connection || {};
    const frugal = conn.saveData === true || /^([23]g|slow-2g)$/.test(conn.effectiveType || '');

    if (!frugal) document.querySelectorAll('[data-video-item]').forEach((item, i) => {
      const entry = feed[i];
      if (!entry || !entry.sources || entry.sources.length === 0) return;
      const media = item.querySelector('.mm-vcard__media');
      if (!media) return;

      const video = document.createElement('video');
      video.className = 'mm-vcard__preview';
      /* Attributes, not just properties. WebKit reads the content attributes
       * when it decides whether a video may start without a gesture, and a
       * script-built element that only has the IDL properties set is judged
       * as unmuted — which is a refusal on every iPhone. */
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('loop', '');
      video.setAttribute('preload', 'none');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;

      /* Smallest acceptable rendition first. The browser takes the first
       * source it believes it can play, not the best one, so 1080p at the top
       * of the list is what a phone downloads — and at 7 Mbps it never
       * finishes, which is what "the videos do not play" looked like. */
      [...entry.sources]
        .sort((x, y) => Math.abs((x.height || 1080) - 720) - Math.abs((y.height || 1080) - 720))
        .forEach((src) => {
          const source = document.createElement('source');
          source.src = src.url;
          source.type = src.type || '';
          video.appendChild(source);
        });

      media.appendChild(video);
      previews.set(item, video);
    });

    if (previews.size) {
      let playing = null;

      /* Start is two steps, not one. A video with preload="none" has no data,
       * and play() on it resolves into a black frame while the network catches
       * up. Ask for the bytes, then play when there are some. */
      function start(video, item) {
        if (playing && playing !== video) {
          playing.pause();
          playing.dataset.wanted = '';
          playing.closest('[data-video-item]')?.classList.remove('is-playing');
        }
        playing = video;
        video.dataset.wanted = '1';

        const go = () => {
          if (video.dataset.wanted !== '1' || !video.isConnected) return;
          const p = video.play();
          if (p && p.then) {
            p.then(
              () => item.classList.add('is-playing'),
              () => item.classList.remove('is-playing')
            );
          } else {
            item.classList.add('is-playing');
          }
        };

        if (video.preload !== 'metadata' && video.preload !== 'auto') {
          video.preload = 'metadata';
          video.load();
        }
        video.addEventListener('canplay', go);
        video.addEventListener('loadeddata', go);
        if (video.readyState >= 2) go();
        else go(); /* harmless if it is refused; canplay will try again */
      }

      /* The card nearest the middle wins, measured rather than inferred. The
       * old version squeezed the observer root 25% in from each side and
       * asked for a 0.6 ratio — on a 375px phone a 263px card can reach 0.71
       * only while it entirely covers that window, a band about 75px wide.
       * It worked on a desktop and was a coin toss on a phone. */
      let raf = 0;
      function pick() {
        raf = 0;
        const mid = window.innerWidth / 2;
        let best = null;
        let bestGap = Infinity;
        previews.forEach((video, item) => {
          const r = item.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight) return;
          const gap = Math.abs((r.left + r.right) / 2 - mid);
          if (gap < bestGap) { bestGap = gap; best = { video, item }; }
        });
        if (!best) {
          if (playing) { playing.pause(); playing.dataset.wanted = ''; }
          return;
        }
        if (best.video !== playing) start(best.video, best.item);
      }
      const schedule = () => { if (!raf) raf = requestAnimationFrame(pick); };

      const track = document.querySelector('[data-video-track]');
      track?.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);

      /* Only run the row while it is on screen at all. */
      const gate = new IntersectionObserver(
        (entries) => {
          const on = entries.some((e) => e.isIntersecting);
          if (on) schedule();
          else if (playing) { playing.pause(); playing.dataset.wanted = ''; }
        },
        { threshold: 0 }
      );
      if (track) gate.observe(track);
      schedule();

      /* The gesture unlock. Safari on iOS will refuse a programmatic play
       * outright until the page has had one real touch; after that the same
       * call is allowed. The reader has to touch something to reach this row,
       * so the first touch anywhere is the moment to ask again. */
      const unlock = () => {
        document.removeEventListener('touchstart', unlock);
        document.removeEventListener('pointerdown', unlock);
        if (playing && playing.paused) playing.play().catch(() => {});
      };
      document.addEventListener('touchstart', unlock, { passive: true, once: true });
      document.addEventListener('pointerdown', unlock, { passive: true, once: true });

      /* A tab in the background should not be decoding video. */
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { if (playing) playing.pause(); }
        else if (playing && playing.dataset.wanted === '1') playing.play().catch(() => {});
      });
    }
  }

  /* ---- The player --------------------------------------------------------
   * Rewritten as a real feed.
   *
   * It used to hold one slide and rebuild it on every move: the video
   * element was destroyed and recreated, so a swipe meant a black frame, a
   * fresh network request and no momentum — it read as a slideshow with a
   * swipe gesture bolted on. Now every slide exists at once in a vertical
   * scroll-snap column, the browser does the swiping, and the only thing
   * this code decides is which slide is centred.
   *
   * Sources are attached to the current slide and its two neighbours only,
   * so opening the feed loads one video rather than six.
   */
  class MaisonVideoPlayer extends HTMLElement {
    connectedCallback() {
      this.index = 0;
      this.muted = true;
      this.opener = null;
      this.built = false;
      this.onKeydown = this.onKeydown.bind(this);
      this.innerHTML = this.template();
      this.stage = this.querySelector('[data-vp-stage]');
      this.bar = this.querySelector('[data-vp-bar]');
      this.count = this.querySelector('[data-vp-count]');
      this.bind();
    }

    template() {
      return `
        <div class="mm-vp__scrim" data-vp-close></div>
        <div class="mm-vp__panel" role="dialog" aria-modal="true" aria-label="Shop by video" tabindex="-1">
          <div class="mm-vp__bar" data-vp-bar></div>
          <header class="mm-vp__head">
            <button class="mm-vp__back" type="button" data-vp-close aria-label="Close video">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>
            </button>
            <span class="mm-vp__title">Shop by video</span>
            <span class="mm-vp__count" data-vp-count>1 / ${feed.length}</span>
          </header>
          <div class="mm-vp__stage" data-vp-stage></div>
        </div>`;
    }

    /* ---- Markup ---------------------------------------------------------- */
    slide(item, i) {
      const p = item.product;
      const hasVideo = item.sources && item.sources.length > 0;

      return `
        <article class="mm-vp__slide" data-vp-slide="${i}">
          <div class="mm-vp__media${hasVideo ? '' : ' is-still'}" data-vp-media>
            ${
              /* No film yet on this block. A packshot cut out on white,
               * cropped to fill a 9:16 frame, is a white wall with a corner
               * of glass in it. Held instead, over a blurred copy of itself,
               * it reads as a frame that was composed rather than one that
               * failed to load. */
              item.poster ? `<img class="mm-vp__bg" src="${item.poster}" alt="" aria-hidden="true">` : ''
            }
            ${
              /* See the page player: the poster attribute is dropped the
               * moment playback starts, so it cannot be the only thing behind
               * a buffering video. */
              hasVideo
                ? `<img class="mm-vp__poster" src="${item.poster || ''}" alt="" aria-hidden="true">
                   <video playsinline loop muted preload="none" poster="${item.poster || ''}" data-vp-video></video>`
                : `<img class="mm-vp__still" src="${item.poster || ''}" alt="">`
            }
            <span class="mm-vp__glyph" data-vp-glyph aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 6.5v11l9-5.5z"/></svg>
            </span>
            <span class="mm-vp__heart" data-vp-heart aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 20s-7-4.6-7-9.3A4 4 0 0 1 12 8a4 4 0 0 1 7 2.7C19 15.4 12 20 12 20z"/></svg>
            </span>
          </div>

          <div class="mm-vp__rail">
            <button class="mm-vp__act" type="button" data-vp-save aria-label="Save" aria-pressed="false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 20s-7-4.6-7-9.3A4 4 0 0 1 12 8a4 4 0 0 1 7 2.7C19 15.4 12 20 12 20z"/></svg>
              <span>Save</span>
            </button>
            <button class="mm-vp__act${this.muted ? ' is-off' : ''}" type="button" data-vp-sound aria-label="Sound">
              <svg class="mm-vp__ison" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 9v6h3.5L13 19V5L8.5 9z"/><path d="M16.5 9.5a3.5 3.5 0 0 1 0 5"/></svg>
              <svg class="mm-vp__isoff" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 9v6h3.5L13 19V5L8.5 9z"/><path d="M17 10l4 4M21 10l-4 4"/></svg>
              <span>Sound</span>
            </button>
            <button class="mm-vp__act" type="button" data-vp-share aria-label="Share">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 15V4"/><path d="M8 8l4-4 4 4"/><path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"/></svg>
              <span>Share</span>
            </button>
            <a class="mm-vp__act mm-vp__act--bag" href="${window.Shopify?.routes?.root || '/'}cart" aria-label="Bag">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5.5 8h13l-1 11.5a1 1 0 0 1-1 .9H7.5a1 1 0 0 1-1-.9z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/></svg>
              <span class="mm-vp__bagcount" data-vp-bagcount>0</span>
            </a>
          </div>

          <div class="mm-vp__foot">
            <p class="mm-vp__handle">@${(window.__mmHandle || 'maisonmilan')}</p>
            <p class="mm-vp__caption">${item.caption || ''}</p>

            ${
              item.link
                ? `<a class="mm-vp__extlink" href="${item.link}" target="_blank" rel="noopener">
                     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M14 5h5v5"/><path d="M19 5l-8 8"/><path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"/></svg>
                     ${item.linkLabel || 'Watch on Instagram'}
                   </a>`
                : ''
            }

            <div class="mm-vp__product">
              <a class="mm-vp__plink" href="${p.url}">
                <span class="mm-vp__pthumb">${p.image ? `<img src="${p.image}" alt="" width="44" height="44">` : ''}</span>
                <span class="mm-vp__pinfo">
                  ${item.tag ? `<span class="mm-vp__ptag">${item.tag}</span>` : ''}
                  <span class="mm-vp__pname">${p.title}</span>
                  <span class="mm-vp__pprice">${money(p.price)}</span>
                </span>
              </a>
              ${
                p.available
                  ? `<form data-vp-form method="post" action="/cart/add">
                       <input type="hidden" name="id" value="${p.variantId}">
                       <button type="submit" class="mm-vp__add">Add to bag</button>
                     </form>`
                  : '<span class="mm-vp__soldout">Sold out</span>'
              }
            </div>
          </div>
        </article>`;
    }

    build() {
      if (this.built) return;
      this.stage.innerHTML = feed.map((item, i) => this.slide(item, i)).join('');
      this.slides = [...this.stage.querySelectorAll('[data-vp-slide]')];
      this.bar.innerHTML = feed.map(() => '<span class="mm-vp__seg"></span>').join('');
      this.segs = [...this.bar.querySelectorAll('.mm-vp__seg')];

      /* Which slide is centred is the browser's answer, not ours: the
       * observer reports it after the snap settles, so the state can never
       * disagree with what is on screen. */
      this.watcher = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting || e.intersectionRatio < 0.6) return;
            const i = parseInt(e.target.getAttribute('data-vp-slide'), 10);
            if (i !== this.index) this.setActive(i);
          });
        },
        { root: this.stage, threshold: [0.6] }
      );
      this.slides.forEach((s) => this.watcher.observe(s));
      this.built = true;
      this.bindLoop();
    }

    /* ---- Sources ---------------------------------------------------------
     * Attached for the centred slide and its neighbours, detached outside
     * that window. Six autoplaying videos on a phone is a stall, not a feed.
     */
    hydrate(centre) {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('[data-vp-video]');
        if (!video) return;
        const near = Math.abs(i - centre) <= 1;

        if (near && !video.dataset.loaded) {
          /* Ordered smallest-acceptable first. The browser takes the first
           * source it can play, not the best one, so 1080p at 7.2 Mbps sitting
           * at the top of the list is what it would load on a phone. 720 is
           * the sweet spot for a 9:16 frame at any width the feed renders. */
          const ordered = [...(feed[i].sources || [])].sort((x, y) => {
            const score = (s) => Math.abs((s.height || 1080) - 720);
            return score(x) - score(y);
          });
          ordered.forEach((src) => {
            const el = document.createElement('source');
            el.src = src.url;
            el.type = src.type || '';
            video.appendChild(el);
          });
          video.preload = 'metadata';
          video.dataset.loaded = '1';
          video.load();
        } else if (!near && video.dataset.loaded) {
          video.pause();
          video.removeAttribute('src');
          while (video.firstChild) video.removeChild(video.firstChild);
          delete video.dataset.loaded;
        }
      });
    }

    setActive(i) {
      this.index = i;
      this.hydrate(i);
      this.count.textContent = `${i + 1} / ${feed.length}`;
      this.segs.forEach((seg, n) => {
        seg.classList.toggle('is-done', n < i);
        seg.classList.toggle('is-live', n === i);
        if (n !== i) seg.style.removeProperty('--p');
      });

      this.slides.forEach((slide, n) => {
        const video = slide.querySelector('[data-vp-video]');
        slide.classList.toggle('is-live', n === i);
        if (!video) return;
        if (n === i) {
          video.muted = this.muted;
          /* Only rewind once it can seek — see the page player. */
          if (video.readyState >= 1) video.currentTime = 0;
          video.dataset.wanted = '1';
          slide.classList.remove('is-paused');
          this.keepPlaying(video);
        } else {
          delete video.dataset.wanted;
          video.pause();
        }
      });

      this.trackProgress();
    }

    /* The segment for the current slide fills with the video rather than
     * jumping when the index changes — that fill is the thing that makes a
     * feed feel like it is playing rather than sitting. */
    trackProgress() {
      const slide = this.slides[this.index];
      const video = slide?.querySelector('[data-vp-video]');
      const seg = this.segs[this.index];
      if (!video || !seg) return;

      if (this.progressOn) this.progressOn.removeEventListener('timeupdate', this.onProgress);
      this.onProgress = () => {
        if (!video.duration) return;
        seg.style.setProperty('--p', `${(video.currentTime / video.duration) * 100}%`);
      };
      video.addEventListener('timeupdate', this.onProgress);
      this.progressOn = video;
    }

    go(next) {
      if (next < 0 || next >= feed.length) return;
      this.slides[next]?.scrollIntoView({
        behavior: reduced.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    /* ---- Gestures on the media ------------------------------------------
     * One tap pauses, two like. They share a timer: a single tap is only a
     * single tap once the window for a second one has passed, which is why
     * the pause is deferred rather than fired immediately.
     */
    bindMedia() {
      let lastTap = 0;
      let pending = null;

      this.stage.addEventListener('click', (event) => {
        if (!event.target.closest('[data-vp-media]')) return;
        if (event.target.closest('button, a, form')) return;

        const now = Date.now();
        if (now - lastTap < 300) {
          clearTimeout(pending);
          lastTap = 0;
          this.like();
          return;
        }
        lastTap = now;
        pending = setTimeout(() => this.togglePlay(), 300);
      });
    }

    togglePlay() {
      const slide = this.slides[this.index];
      const video = slide?.querySelector('[data-vp-video]');
      if (!video) return;
      if (video.paused) {
        video.play().catch(() => {});
        slide.classList.remove('is-paused');
      } else {
        video.pause();
        slide.classList.add('is-paused');
      }
    }

    like() {
      const slide = this.slides[this.index];
      if (!slide) return;
      slide.querySelector('[data-vp-save]')?.setAttribute('aria-pressed', 'true');
      slide.querySelector('[data-vp-save]')?.classList.add('is-on');
      const heart = slide.querySelector('[data-vp-heart]');
      if (!heart) return;
      heart.classList.remove('is-burst');
      void heart.offsetWidth;
      heart.classList.add('is-burst');
    }

    /* ---- Endless -----------------------------------------------------------
     * A reel feed has no last video.
     *
     * Two earlier attempts watched for the user pushing past the end — first
     * the `scroll` event, then `wheel` and `touchmove`. Neither is sound.
     * Once a scroller is pinned at its end it cannot move, so it stops
     * producing scroll events; and a wheel listener only sees a wheel, so a
     * trackpad fling, a scrollbar drag or a keyboard PageDown all miss it.
     *
     * A clone of the first slide sits after the last instead. Reaching the
     * end is then an ordinary scroll *within* range, which every input
     * produces, and landing on the clone hands back to the real first slide.
     * The jump is invisible because the two frames are identical.
     */
    /* ---- Keeping the active film playing ------------------------------------
     * play() is a promise that can be rejected or simply deferred, and one
     * call is not enough: a hidden tab will not start playback at all and
     * nothing resumes it, play() in the same tick as load() can be
     * interrupted by the load, and a slide can go active before it has the
     * data to start. The intent is recorded and re-asserted instead.
     */
    keepPlaying(video) {
      if (!video) return;
      const tryPlay = () => {
        if (document.visibilityState !== 'visible') return;
        if (!video.isConnected || video.dataset.wanted !== '1') return;
        const p = video.play();
        if (p && typeof p.catch === 'function') {
          p.then(
            () => video.closest('.mm-vp__slide')?.classList.remove('is-paused'),
            () => {
              /* Autoplay refused — see the page player. The glyph is what
               * tells the reader the frame is a film they can start. */
              video.closest('.mm-vp__slide')?.classList.add('is-paused');
            }
          );
        }
      };
      video.addEventListener('canplay', tryPlay);
      video.addEventListener('loadeddata', tryPlay);
      tryPlay();
    }

    bindVisibility() {
      this.onVisibility = () => {
        const all = this.stage.querySelectorAll('video');
        if (document.visibilityState !== 'visible') {
          all.forEach((v) => v.pause());
          return;
        }
        all.forEach((v) => {
          if (v.dataset.wanted !== '1') return;
          const p = v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        });
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    bindLoop() {
      if (!this.slides || this.slides.length < 2) return;

      const clone = this.slides[0].cloneNode(true);
      clone.setAttribute('data-vp-clone', '');
      clone.removeAttribute('data-vp-slide');
      clone.querySelectorAll('video').forEach((v) => v.remove());
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input').forEach((el) => {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      });
      this.stage.appendChild(clone);

      let wrapping = false;
      this.loopWatcher = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting || e.intersectionRatio < 0.6 || wrapping) return;
            wrapping = true;
            this.slides[0].scrollIntoView({ block: 'start', behavior: 'auto' });
            this.setActive(0);
            setTimeout(() => { wrapping = false; }, 300);
          });
        },
        { root: this.stage, threshold: [0.6] }
      );
      this.loopWatcher.observe(clone);
    }

    bind() {
      this.querySelectorAll('[data-vp-close]').forEach((b) => b.addEventListener('click', () => this.close()));

      this.stage.addEventListener('click', (event) => {
        if (event.target.closest('[data-vp-sound]')) return this.toggleSound();
        if (event.target.closest('[data-vp-share]')) return this.share();
        const save = event.target.closest('[data-vp-save]');
        if (save) {
          const on = save.classList.toggle('is-on');
          save.setAttribute('aria-pressed', String(on));
          return;
        }
      });

      this.stage.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-vp-form]');
        if (!form) return;
        event.preventDefault();
        this.add(form);
      });

      this.bindMedia();
      this.bindVisibility();
    }

    open(index, opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      this.scrollY = window.scrollY;

      this.siblings = [...document.body.children].filter((el) => el !== this && !el.hasAttribute('inert'));
      this.siblings.forEach((el) => el.setAttribute('inert', ''));
      document.documentElement.classList.add('mm-vp-lock');
      document.body.style.top = `-${this.scrollY}px`;

      this.build();

      const start = Math.max(0, Math.min(index, feed.length - 1));
      /* Jumped to, not scrolled to: the feed has just been revealed and a
       * smooth scroll from slide 0 would play every video on the way. */
      this.slides[start]?.scrollIntoView({ block: 'start', behavior: 'auto' });
      this.setActive(start);

      /* iOS will not let script start a video until the page has had a real
       * touch. Opening this player is one — but the gesture is spent by the
       * time setActive runs, so ask again on the next one. */
      const unlock = () => {
        const live = this.stage.querySelector('.mm-vp__slide.is-live [data-vp-video]');
        if (live && live.paused && live.dataset.wanted === '1') live.play().catch(() => {});
      };
      document.addEventListener('touchstart', unlock, { passive: true, once: true });
      document.addEventListener('pointerdown', unlock, { passive: true, once: true });

      requestAnimationFrame(() => this.setAttribute('data-open', ''));
      document.addEventListener('keydown', this.onKeydown);
      setTimeout(() => this.querySelector('.mm-vp__panel')?.focus({ preventScroll: true }), 60);
    }

    close() {
      this.removeAttribute('data-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.siblings?.forEach((el) => el.removeAttribute('inert'));
      document.documentElement.classList.remove('mm-vp-lock');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY || 0);
      this.stage.querySelectorAll('video').forEach((v) => v.pause());
      setTimeout(() => { this.hidden = true; }, 300);
      this.opener?.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key === 'ArrowDown') { event.preventDefault(); this.go(this.index + 1); }
      if (event.key === 'ArrowUp') { event.preventDefault(); this.go(this.index - 1); }
      if (event.key === ' ') { event.preventDefault(); this.togglePlay(); }
      if (event.key === 'm' || event.key === 'M') this.toggleSound();
    }

    toggleSound() {
      this.muted = !this.muted;
      this.stage.querySelectorAll('video').forEach((v) => { v.muted = this.muted; });
      this.stage.querySelectorAll('[data-vp-sound]').forEach((b) => b.classList.toggle('is-off', this.muted));
    }

    share() {
      const item = feed[this.index];
      const url = new URL(item.product.url, location.origin).href;
      /* navigator.share is refused in a lot of embedded views, so the
       * fallback is the one that has to work, not the nicety. */
      if (navigator.share) {
        navigator.share({ title: item.product.title, url }).catch(() => {});
        return;
      }
      navigator.clipboard?.writeText(url).then(
        () => this.toast('Link copied'),
        () => this.toast(url)
      );
    }

    async add(form) {
      const button = form.querySelector('button[type="submit"]');
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        const body = new FormData(form);
        await fetch('/cart/add.js', { method: 'POST', body });
        button.textContent = 'Added';
        this.bumpBag();
        /* Dawn's cart sections are what the header and the drawer read, so
         * they are refreshed rather than the count being written by hand. */
        const res = await fetch('/?sections=cart-icon-bubble');
        const json = await res.json();
        const bubble = document.querySelector('#cart-icon-bubble');
        if (bubble && json['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(json['cart-icon-bubble'], 'text/html');
          const fresh = parsed.querySelector('#cart-icon-bubble');
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }
        setTimeout(() => { button.textContent = label; button.disabled = false; }, 1600);
      } catch (e) {
        button.textContent = 'Try again';
        button.disabled = false;
      }
    }

    bumpBag() {
      this.querySelectorAll('[data-vp-bagcount]').forEach((count) => {
        count.textContent = (parseInt(count.textContent, 10) || 0) + 1;
        count.classList.remove('is-bump');
        void count.offsetWidth;
        count.classList.add('is-bump');
      });
    }

    toast(message) {
      let t = this.querySelector('.mm-vp__toast');
      if (!t) {
        t = document.createElement('p');
        t.className = 'mm-vp__toast';
        t.setAttribute('role', 'status');
        this.querySelector('.mm-vp__panel').appendChild(t);
      }
      t.textContent = message;
      t.classList.add('is-on');
      setTimeout(() => t.classList.remove('is-on'), 2200);
    }
  }

  customElements.define('maison-video-player', MaisonVideoPlayer);

  const player = document.createElement('maison-video-player');
  player.className = 'mm-vp';
  player.hidden = true;
  document.body.appendChild(player);

  document.querySelectorAll('[data-video-open]').forEach((btn) => {
    btn.addEventListener('click', () => player.open(parseInt(btn.getAttribute('data-video-open'), 10) || 0, btn));
  });
})();
